import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import {
  clearDealItemsInProgressToast,
  getDatasourceProcessedToastContent,
  replaceDealItemsInProgressWithSuccess,
} from '@/lib/deal-items-toast';
import {
  DataSourceStatus,
  GetDatasourceDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { useQuery } from '@apollo/client/react';

export function DealGenerationProgress({
  dealId,
  sourceId,
  onSettled,
}: {
  dealId: string;
  sourceId: string;
  onSettled: () => void;
}) {
  const { t } = useTranslation();
  const settled = useRef(false);
  const { data } = useQuery(GetDatasourceDocument, {
    variables: { dealId },
    fetchPolicy: 'network-only',
    pollInterval: 2_000,
    skipPollAttempt: () => document.hidden,
  });
  const source =
    data?.deal?.id === dealId
      ? data.deal.dataSources.find((source) => source.id === sourceId)
      : undefined;

  useEffect(() => {
    if (settled.current || !source) return;
    if (
      source.status !== DataSourceStatus.Processed &&
      source.status !== DataSourceStatus.Error
    )
      return;
    settled.current = true;
    if (source.status === DataSourceStatus.Processed) {
      replaceDealItemsInProgressWithSuccess(
        getDatasourceProcessedToastContent(source, t),
        { datasourceId: sourceId }
      );
    } else {
      trackEvent('NEGOTIATION_FILE_GENERATION_FAILED', {
        dealId,
        surface: 'deals_list',
        step: 'import',
      });
      clearDealItemsInProgressToast({ datasourceId: sourceId });
      toast({
        title: t('general.error_import'),
        description: t('deals-list.generationImportFailed'),
        variant: 'destructive',
      });
    }
    onSettled();
  }, [dealId, source, sourceId, t, onSettled]);

  return null;
}
